import { useEffect, useRef, useState } from 'react';
import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/24/solid';
import {
  Building2,
  Eye,
  EyeOff,
  MapPin,
  Plane,
} from 'lucide-react';
import { useClickOutside } from 'services/useClickOutside';
import CountryBadge from '../../moduleComponents/CountryBadge';
import { findCountryData, initializeCountryRegions } from '../../moduleServices/trips_sanitize';
import useTravelDateSortDescending from '../../moduleServices/useTravelDateSortDescending';
import useTravelTimelineVisibility from '../../moduleServices/useTravelTimelineVisibility';
import { buildDayTimeline } from './itineraryFormUtils';

const PLACE_MODE_LABELS = {
  all: 'All',
  first: 'First',
  last: 'Last',
  none: 'None',
};

/** Display order in the eye menu (All first = default). */
const PLACE_MODE_OPTIONS = ['all', 'first', 'last', 'none'];

function DateChip({ iso }) {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const monthDay = dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const weekday = dt.toLocaleDateString('en-US', { weekday: 'short' });

  return (
    <div
      className="flex flex-col items-center justify-center w-14 h-[2.85rem] px-1 rounded-xl bg-module-travel text-white text-center leading-tight shadow-sm z-[1] tabular-nums"
      aria-label={dt.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
    >
      <span className="text-sm font-semibold whitespace-nowrap">{monthDay}</span>
      <span className="text-[10px] opacity-80">{weekday}</span>
    </div>
  );
}

function YearBreak({ year }) {
  return (
    <div className="flex items-center gap-2 py-2 mb-1">
      <div className="w-14 flex-shrink-0 flex justify-center">
        <span className="text-xs font-semibold text-module-travel tabular-nums">{year}</span>
      </div>
      <div className="flex-1 h-px bg-module-travel/20" />
    </div>
  );
}

function StayChip({ stayMeta, onClick }) {
  const { item, isCheckin, isCheckout } = stayMeta;
  let tag = 'night';
  if (isCheckin && isCheckout) tag = 'in/out';
  else if (isCheckin) tag = 'check-in';
  else if (isCheckout) tag = 'check-out';

  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 max-w-full text-left rounded-md bg-emerald-50/80 hover:bg-emerald-100 border border-emerald-100 px-2 py-1 transition"
    >
      <span className="text-xs font-medium text-emerald-900 truncate">
        {item.name || 'Stay'}
      </span>
      <span className="text-[10px] uppercase tracking-wide text-emerald-700/80 flex-shrink-0">
        {tag}
      </span>
    </button>
  );
}

function LegChip({ legMeta, onClick }) {
  const { item, isDeparture, isArrival } = legMeta;
  let tag = 'leg';
  if (isDeparture && isArrival) tag = 'dep/arr';
  else if (isDeparture) tag = 'depart';
  else if (isArrival) tag = 'arrive';

  const time = isDeparture
    ? item.departure_time
    : isArrival
      ? item.arrival_time
      : '';
  const label = item.description || item.carrier || item.mode || 'Transport';

  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 max-w-full text-left rounded-md bg-sky-50/80 hover:bg-sky-100 border border-sky-100 px-2 py-1 transition"
    >
      <span className="text-xs font-medium text-sky-900 truncate">{label}</span>
      {time ? <span className="text-[10px] text-sky-700/80 flex-shrink-0">{time}</span> : null}
      <span className="text-[10px] uppercase tracking-wide text-sky-700/80 flex-shrink-0">
        {tag}
      </span>
    </button>
  );
}

/** Places to show for a day given placeMode (sorted by arrival_date asc). */
function placesForMode(places, placeMode) {
  if (!places?.length || placeMode === 'none') return [];
  const sorted = [...places].sort((a, b) =>
    String(a.arrival_date || '').localeCompare(String(b.arrival_date || ''))
  );
  if (placeMode === 'first') return [sorted[0]];
  if (placeMode === 'last') return [sorted[sorted.length - 1]];
  return sorted;
}

function PlaceLabel({ place }) {
  const countryData = place.country ? findCountryData(place.country, 'iso3') : null;
  const countryLabel = place.country
    ? (countryData?.iso3 || place.country)
    : null;
  const placeLabel = [countryLabel, place.name].filter(Boolean).join(' · ');
  if (!placeLabel) return null;

  return (
    <span className="inline-flex items-center gap-1.5 max-w-full min-w-0">
      {countryData?.iso2 ? (
        <CountryBadge code={countryData.iso2} size={14} />
      ) : null}
      <span className="text-xs font-semibold text-gray-900 truncate">{placeLabel}</span>
    </span>
  );
}

function DayNode({
  day,
  isLast,
  onOpenStay,
  onOpenTransport,
  placeMode,
  showStays,
  showTransports,
}) {
  const visiblePlaces = placesForMode(day.places, placeMode);
  const visibleLegs = showTransports ? day.legs : [];
  const visibleStays = showStays ? day.stays : [];
  const hasPlaces = visiblePlaces.length > 0;
  const hasLogistics = visibleStays.length > 0 || visibleLegs.length > 0;
  const hasContent = day.places.length > 0 || day.stays.length > 0 || day.legs.length > 0;
  const showEmptyHint = hasContent && !hasPlaces && !hasLogistics;

  return (
    <div className="flex gap-3 w-full">
      <div className="flex flex-col items-center w-14 flex-shrink-0">
        <DateChip iso={day.date} />
        {!isLast && <div className="w-0.5 flex-1 min-h-[1.25rem] bg-module-travel/25 -mt-0.5" />}
      </div>

      <div className={`min-w-0 flex-1 pt-0.5 ${isLast ? 'pb-1' : 'pb-7'}`}>
        {hasPlaces ? (
          <div className="flex items-start gap-1.5 min-w-0">
            <MapPin className="h-3.5 w-3.5 text-module-travel flex-shrink-0 mt-0.5" strokeWidth={2} />
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
              {visiblePlaces.map((place, i) => (
                <PlaceLabel
                  key={place.id || `${place.name}-${place.country}-${place.arrival_date}-${i}`}
                  place={place}
                />
              ))}
            </div>
          </div>
        ) : null}

        {showEmptyHint && (
          <p className={`text-[11px] text-gray-400 italic ${hasPlaces ? 'mt-1.5' : ''}`}>
            Hidden by visibility filters
          </p>
        )}

        {!hasContent && (
          <p className="text-[11px] text-gray-400 italic">No logistics this day</p>
        )}

        {visibleLegs.length > 0 && (
          <div className={`${hasPlaces ? 'mt-1.5' : ''} flex items-start gap-1.5 min-w-0`}>
            <Plane className="h-3.5 w-3.5 text-sky-700 flex-shrink-0 mt-1.5" strokeWidth={2} />
            <div className="flex flex-wrap gap-1.5 min-w-0">
              {visibleLegs.map((legMeta) => (
                <LegChip
                  key={`leg-${legMeta.item._tempId || legMeta.item.id}-${legMeta.isDeparture}-${legMeta.isArrival}`}
                  legMeta={legMeta}
                  onClick={() => onOpenTransport?.(legMeta.item)}
                />
              ))}
            </div>
          </div>
        )}

        {visibleStays.length > 0 && (
          <div className={`${hasPlaces || visibleLegs.length > 0 ? 'mt-1.5' : ''} flex items-start gap-1.5 min-w-0`}>
            <Building2 className="h-3.5 w-3.5 text-emerald-700 flex-shrink-0 mt-1.5" strokeWidth={2} />
            <div className="flex flex-wrap gap-1.5 min-w-0">
              {visibleStays.map((stayMeta) => (
                <StayChip
                  key={`stay-${stayMeta.item._tempId || stayMeta.item.id}`}
                  stayMeta={stayMeta}
                  onClick={() => onOpenStay?.(stayMeta.item)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TimelineVisibilityMenu({ visibility, patchVisibility, allLayersHidden }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  useClickOutside([rootRef], () => setOpen(false), open);

  const EyeIcon = allLayersHidden ? EyeOff : Eye;

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`p-1.5 rounded-lg transition-colors ${
          open || allLayersHidden
            ? 'text-module-travel bg-gray-100'
            : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
        }`}
        title="Day visibility"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <EyeIcon className="h-4 w-4" strokeWidth={2} />
      </button>

      {open && (
        <div
          className="absolute left-0 top-full mt-1 z-20 w-52 rounded-lg border border-gray-200 bg-white shadow-lg py-2 text-sm"
          role="menu"
        >
          <div className="px-3 pb-2 mb-1 border-b border-gray-100">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">
              Place
            </p>
            <div className="space-y-0.5">
              {PLACE_MODE_OPTIONS.map((mode) => (
                <label
                  key={mode}
                  className="flex items-center gap-2 px-1 py-1 rounded hover:bg-gray-50 cursor-pointer select-none"
                >
                  <input
                    type="radio"
                    name="timeline-place-mode"
                    checked={visibility.placeMode === mode}
                    onChange={() => patchVisibility({ placeMode: mode })}
                    className="border-gray-300 text-module-travel focus:ring-module-travel/30"
                  />
                  <span className="text-gray-700">{PLACE_MODE_LABELS[mode]}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="px-3 pt-1 space-y-0.5">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">
              Logistics
            </p>
            <label className="flex items-center gap-2 px-1 py-1 rounded hover:bg-gray-50 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={visibility.showStays}
                onChange={(e) => patchVisibility({ showStays: e.target.checked })}
                className="rounded border-gray-300 text-module-travel focus:ring-module-travel/30"
              />
              <span className="text-gray-700">Stays</span>
            </label>
            <label className="flex items-center gap-2 px-1 py-1 rounded hover:bg-gray-50 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={visibility.showTransports}
                onChange={(e) => patchVisibility({ showTransports: e.target.checked })}
                className="rounded border-gray-300 text-module-travel focus:ring-module-travel/30"
              />
              <span className="text-gray-700">Transports</span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Day-by-day timeline from trip dates/places and trip stays/transports.
 */
const ItineraryDayTimeline = ({
  trip,
  transports,
  stays,
  onOpenTransport,
  onOpenStay,
}) => {
  const [sortDescending, setSortDescending] = useTravelDateSortDescending(true);
  const { visibility, patchVisibility, allLayersHidden } = useTravelTimelineVisibility();
  const [, setRegionsReady] = useState(false);

  useEffect(() => {
    initializeCountryRegions().then(() => setRegionsReady(true));
  }, []);

  if (!trip) {
    return (
      <div className="px-2 py-10 text-center">
        <MapPin className="h-8 w-8 text-gray-300 mx-auto mb-3" strokeWidth={1.75} />
        <p className="text-sm text-gray-700 font-medium">
          Save the trip to use Timeline
        </p>
        <p className="text-xs text-gray-500 mt-1.5 max-w-xs mx-auto leading-relaxed">
          This view builds day by day from the trip dates, places, stays, and transports.
        </p>
      </div>
    );
  }

  const { days, error } = buildDayTimeline(trip, transports, stays);

  if (error === 'missing_trip_dates') {
    return (
      <p className="text-sm text-gray-500 text-center py-8 px-2">
        Trip is missing start or end dates.
      </p>
    );
  }

  if (days.length === 0) {
    return (
      <p className="text-sm text-gray-400 italic text-center py-8">
        No days in this trip range.
      </p>
    );
  }

  const orderedDays = sortDescending ? [...days].reverse() : days;

  return (
    <div className="px-1 pt-1">
      <div className="flex items-center justify-between gap-3 mb-2">
        <TimelineVisibilityMenu
          visibility={visibility}
          patchVisibility={patchVisibility}
          allLayersHidden={allLayersHidden}
        />
        <button
          type="button"
          onClick={() => setSortDescending((prev) => !prev)}
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-black flex-shrink-0"
        >
          {sortDescending ? (
            <>
              <ArrowDownIcon className="h-4 w-4" />
              Newer
            </>
          ) : (
            <>
              <ArrowUpIcon className="h-4 w-4" />
              Older
            </>
          )}
        </button>
      </div>
      {orderedDays.map((day, idx) => {
        const year = Number(String(day.date).slice(0, 4));
        const prevYear =
          idx > 0 ? Number(String(orderedDays[idx - 1].date).slice(0, 4)) : null;
        const showYearBreak = idx === 0 || year !== prevYear;

        return (
          <div key={day.date}>
            {showYearBreak && <YearBreak year={year} />}
            <DayNode
              day={day}
              isLast={idx === orderedDays.length - 1}
              onOpenStay={onOpenStay}
              onOpenTransport={onOpenTransport}
              placeMode={visibility.placeMode}
              showStays={visibility.showStays}
              showTransports={visibility.showTransports}
            />
          </div>
        );
      })}
    </div>
  );
};

export default ItineraryDayTimeline;
